import { useState, useEffect } from 'react'
import { useTranslation } from 'react-i18next'
import { useAuth } from '../../contexts/AuthContext'
import { APP_API_SERVICE_NAME } from '../../constants/brand'
import {
  setApiBase,
  getConfiguredApiBase,
  getVideoSimulatorMode,
  setVideoSimulatorMode,
} from '../../api/client'
import {
  setSupabaseUrl,
  setSupabaseAnonKey,
  setGotrueUrl,
  getConfiguredSupabaseUrl,
  getConfiguredSupabaseAnonKey,
  getConfiguredGotrueUrl,
  hasSupabaseConfig,
  reinitializeSupabase,
} from '../../utils/supabase'
import ApiKeysSection from './ApiKeysSection'
import DirectorModelSettingsSection from './DirectorModelSettingsSection'
import GatewaySettingsSection from './GatewaySettingsSection'
import { canSeeMetaOnlyUi } from '../../utils/metaEmail'
import ProfileVideoDefaultSection from './ProfileVideoDefaultSection'

interface SettingsFormProps {
  onSave?: () => void
  compact?: boolean
  gatewayOnly?: boolean
}

export default function SettingsForm({ onSave, compact = false, gatewayOnly = false }: SettingsFormProps) {
  const { t } = useTranslation('settings')
  const { session, user } = useAuth()
  const canManageRestrictedByom = canSeeMetaOnlyUi(user)
  const [apiUrl, setApiUrl] = useState('')
  const [tokenCopied, setTokenCopied] = useState(false)

  // Supabase config
  const [supabaseUrlInput, setSupabaseUrlInput] = useState('')
  const [supabaseAnonKeyInput, setSupabaseAnonKeyInput] = useState('')
  // Optional GoTrue URL override — point auth at a self-hosted GoTrue
  // (Cloud Run *.run.app or LB hostname) while keeping SUPABASE_URL pointed
  // at the existing data backend. RFC-0629 Stage 0 dev rehearsal workaround.
  const [gotrueUrlInput, setGotrueUrlInput] = useState('')
  const [supabaseConfigSet, setSupabaseConfigSet] = useState(false)
  const [supabaseMessage, setSupabaseMessage] = useState<{ type: 'success' | 'error'; text: string } | null>(null)

  // Video simulator mode (returns placeholder videos instead of calling real APIs)
  const [videoSimulatorMode, setVideoSimulatorModeState] = useState(false)


  useEffect(() => {
    setApiUrl(getConfiguredApiBase())
    // Supabase config
    setSupabaseUrlInput(getConfiguredSupabaseUrl())
    setSupabaseAnonKeyInput(getConfiguredSupabaseAnonKey())
    setGotrueUrlInput(getConfiguredGotrueUrl())
    setSupabaseConfigSet(hasSupabaseConfig())
    setVideoSimulatorModeState(getVideoSimulatorMode())
  }, [])

  const handleSaveApiUrl = () => {
    let url = apiUrl.trim()
    // Auto-add https:// if missing protocol (Railway and production APIs require HTTPS)
    if (url && !url.startsWith('http://') && !url.startsWith('https://')) {
      url = 'https://' + url
    }
    // Remove trailing slash to prevent double-slash URLs (e.g., //projects)
    url = url.replace(/\/+$/, '')
    setApiBase(url)
    onSave?.()
    // Navigate to home page to refresh with new API endpoint
    window.location.href = '/'
  }

  const handleSaveSupabaseConfig = () => {
    if (!supabaseUrlInput.trim() || !supabaseAnonKeyInput.trim()) {
      setSupabaseMessage({ type: 'error', text: t('settingsForm.bothRequired') })
      return
    }

    let url = supabaseUrlInput.trim()
    // Auto-add https:// if missing protocol
    if (url && !url.startsWith('http://') && !url.startsWith('https://')) {
      url = 'https://' + url
    }
    setSupabaseUrl(url)
    setSupabaseAnonKey(supabaseAnonKeyInput.trim())

    // Optional GoTrue URL override (empty = no override, use SUPABASE_URL/auth/v1).
    let gotrueUrl = gotrueUrlInput.trim()
    if (gotrueUrl && !gotrueUrl.startsWith('http://') && !gotrueUrl.startsWith('https://')) {
      gotrueUrl = 'https://' + gotrueUrl
    }
    setGotrueUrl(gotrueUrl)

    reinitializeSupabase()
    setSupabaseConfigSet(true)
    setSupabaseMessage({ type: 'success', text: t('settingsForm.supabaseConfigSaved') })
    onSave?.()
    // Reload to reinitialize auth
    setTimeout(() => {
      window.location.href = '/'
    }, 1000)
  }

  const handleClearSupabaseConfig = () => {
    setSupabaseUrl('')
    setSupabaseAnonKey('')
    setGotrueUrl('')
    reinitializeSupabase()
    setSupabaseUrlInput('')
    setSupabaseAnonKeyInput('')
    setGotrueUrlInput('')
    setSupabaseConfigSet(false)
    setSupabaseMessage({ type: 'success', text: t('settingsForm.supabaseConfigCleared') })
    setTimeout(() => {
      window.location.href = '/'
    }, 1000)
  }

  const handleCopyToken = async () => {
    if (!session?.access_token) return
    try {
      await navigator.clipboard.writeText(session.access_token)
      setTokenCopied(true)
      setTimeout(() => setTokenCopied(false), 2000)
    } catch (err) {
      console.error('Failed to copy token:', err)
    }
  }

  if (gatewayOnly) {
    return (
      <div className={`space-y-5 ${compact ? '' : 'p-4'}`}>
        {canManageRestrictedByom && <DirectorModelSettingsSection analyticsEntryPoint="settings" />}
        <ProfileVideoDefaultSection />
        <GatewaySettingsSection analyticsEntryPoint="settings" />
        {canManageRestrictedByom && (
          <GatewaySettingsSection modelKind="image" analyticsEntryPoint="settings" />
        )}
      </div>
    )
  }

  return (
    <div className={`space-y-5 ${compact ? '' : 'p-4'}`}>
      {/* API Endpoint */}
      <div>
        <label className="block text-sm text-text-secondary mb-1">{t('settingsForm.apiEndpoint')}</label>
        <input
          type="text"
          value={apiUrl}
          onChange={(e) => setApiUrl(e.target.value)}
          placeholder="http://localhost:9000"
          className="w-full px-3 py-2 bg-surface-dark rounded border border-white/10 text-sm focus:outline-none focus:border-primary"
        />
        <p className="text-xs font-medium text-text-muted mt-1">
          {t('settingsForm.localServer', { serviceName: APP_API_SERVICE_NAME })}
        </p>
        <button
          onClick={handleSaveApiUrl}
          className="mt-2 w-full px-3 py-2 bg-primary hover:bg-primary-light rounded text-sm font-medium transition-colors"
        >
          {t('settingsForm.saveAndReload')}
        </button>
      </div>

      <div className="border-t border-white/10" />

      {/* Supabase Configuration */}
      <div>
        <div className="flex items-center justify-between mb-1">
          <label className="text-sm text-text-secondary">{t('settingsForm.authentication')}</label>
          {supabaseConfigSet && (
            <span className="text-xs font-medium text-green-400 flex items-center gap-1">
              <svg className="w-3 h-3" fill="currentColor" viewBox="0 0 20 20">
                <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
              </svg>
              {t('settingsForm.configured')}
            </span>
          )}
        </div>
        <input
          type="text"
          value={supabaseUrlInput}
          onChange={(e) => setSupabaseUrlInput(e.target.value)}
          placeholder="https://your-project.supabase.co"
          className="w-full px-3 py-2 bg-surface-dark rounded border border-white/10 text-sm focus:outline-none focus:border-primary mb-2"
        />
        <input
          type="password"
          value={supabaseAnonKeyInput}
          onChange={(e) => setSupabaseAnonKeyInput(e.target.value)}
          placeholder="Supabase anon key"
          className="w-full px-3 py-2 bg-surface-dark rounded border border-white/10 text-sm focus:outline-none focus:border-primary"
        />
        <input
          type="text"
          value={gotrueUrlInput}
          onChange={(e) => setGotrueUrlInput(e.target.value)}
          placeholder="GoTrue URL override (optional, e.g. https://dreamwright-gotrue-xxx-uc.a.run.app)"
          className="w-full px-3 py-2 bg-surface-dark rounded border border-white/10 text-sm focus:outline-none focus:border-primary mt-2"
        />
        <p className="text-xs font-medium text-text-muted mt-1">
          {t('settingsForm.supabaseHelpPrefix')}{' '}
          <a
            href="https://supabase.com/dashboard"
            target="_blank"
            rel="noopener noreferrer"
            className="text-primary hover:text-primary-light"
          >
            {t('settingsForm.supabaseProjectSettings')}
          </a>
          . <span className="text-text-muted">{t('settingsForm.gotrueHelp')}</span>
        </p>
        {supabaseMessage && (
          <p className={`text-xs font-medium mt-1 ${supabaseMessage.type === 'success' ? 'text-green-400' : 'text-red-400'}`}>
            {supabaseMessage.text}
          </p>
        )}
        <div className="flex gap-2 mt-2">
          <button
            onClick={handleSaveSupabaseConfig}
            disabled={!supabaseUrlInput.trim() || !supabaseAnonKeyInput.trim()}
            className="flex-1 px-3 py-2 bg-primary hover:bg-primary-light disabled:opacity-50 disabled:cursor-not-allowed rounded text-sm font-medium transition-colors"
          >
            {t('settingsForm.saveAndReload')}
          </button>
          {supabaseConfigSet && (
            <button
              onClick={handleClearSupabaseConfig}
              className="px-3 py-2 bg-red-500/20 hover:bg-red-500/30 text-red-400 rounded text-sm transition-colors"
            >
              {t('settingsForm.clear')}
            </button>
          )}
        </div>

        {/* Copy JWT Token - only shown when authenticated */}
        {session?.access_token && (
          <div className="mt-4 p-3 rounded-lg border border-white/10 bg-surface-dark">
            <div className="flex items-center justify-between">
              <div>
                <label className="block text-sm text-text-secondary">{t('settingsForm.jwtAccessToken')}</label>
                <p className="text-xs font-medium text-text-muted mt-1">
                  {t('settingsForm.copyForDebugging')}
                </p>
              </div>
              <button
                onClick={handleCopyToken}
                className={`px-3 py-1.5 rounded text-sm font-medium transition-colors flex items-center gap-1.5 ${
                  tokenCopied
                    ? 'bg-green-500/20 text-green-400'
                    : 'bg-surface-2 hover:bg-border text-foreground'
                }`}
              >
                {tokenCopied ? (
                  <>
                    <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                    </svg>
                    {t('settingsForm.copied')}
                  </>
                ) : (
                  <>
                    <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z" />
                    </svg>
                    {t('settingsForm.copyToken')}
                  </>
                )}
              </button>
            </div>
          </div>
        )}
      </div>

      {/* Per-User API Keys - only shown when logged in. The section itself
          re-checks `session` (see ApiKeysSection); this outer gate keeps the
          divider from rendering on its own for a signed-out user. */}
      {session && (
        <>
          <div className="border-t border-white/10" />
          <ApiKeysSection />
        </>
      )}

      <div className="border-t border-white/10" />

      {/* AI Gateway Section */}
      {canManageRestrictedByom && <DirectorModelSettingsSection analyticsEntryPoint="settings" />}

      {canManageRestrictedByom && <div className="border-t border-white/10" />}

      <GatewaySettingsSection analyticsEntryPoint="settings" />

      {canManageRestrictedByom && <div className="border-t border-white/10" />}

      {canManageRestrictedByom && (
        <GatewaySettingsSection modelKind="image" analyticsEntryPoint="settings" />
      )}

      <div className="border-t border-white/10" />

      {/* Short Drama Video Section */}
      <div>
        <h3 className="text-sm font-medium text-foreground mb-3">{t('settingsForm.shortDramaVideo')}</h3>
        <p className="text-xs font-medium text-text-muted mb-4">
          {t('settingsForm.chooseModel')}
        </p>

        {/* Video Simulator Mode Toggle */}
        <div className="mb-4 p-3 rounded-lg border border-purple-500/30 bg-purple-500/10">
          <div className="flex items-center justify-between">
            <div>
              <label className="block text-sm font-medium text-foreground">{t('settingsForm.videoSimulatorMode')}</label>
              <p className="text-xs font-medium text-text-secondary mt-1">
                {t('settingsForm.videoSimulatorDescription')}
              </p>
            </div>
            <button
              onClick={() => {
                const newValue = !videoSimulatorMode
                setVideoSimulatorMode(newValue)
                setVideoSimulatorModeState(newValue)
              }}
              className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
                videoSimulatorMode ? 'bg-purple-500' : 'bg-border'
              }`}
            >
              <span
                className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                  videoSimulatorMode ? 'translate-x-6' : 'translate-x-1'
                }`}
              />
            </button>
          </div>
          {videoSimulatorMode && (
            <div className="mt-2 flex items-center gap-2 text-xs font-medium text-purple-300">
              <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
              {t('settingsForm.videoSimulatorActive')}
            </div>
          )}
        </div>

        {/* Video model is now selected per-node on VideoGeneratorNode */}
      </div>

      <div className="border-t border-white/10" />

      {/* Experimental Features */}
      <div>
        <h3 className="text-sm font-medium text-foreground mb-3">{t('settingsForm.experimentalFeatures')}</h3>
        <p className="text-xs font-medium text-text-muted mb-4">
          {t('settingsForm.experimentalDescription')}
        </p>

      </div>
    </div>
  )
}
